import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import PreviewBody from "@/components/Every24HoursReportPreviewParts/PreviewBody.vue";
import ButtonArea from "@/components/Every24HoursReportPreviewParts/ButtonArea.vue";
import every24HoursPreviewType from "@/types/responses/every24HoursPreview";
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi";
import { useAuth } from "@/services/user/Auth0UserProvider";
import { Ref, ref, onMounted } from "vue";
import Api from "@/services/api/ApiServiceFabrick";
import { useRoute, useRouter } from "vue-router";
import i18n from "@/lang/i18n";
import {
  EVERY_24_HOUR_REPORT_PREVIEW_TYPE,
  EVERY_24_HOUR_REPORT_TYPE
} from "@/services/consts/every_24_hours_report";
import {
  JOB_STATUS_SUCCESS,
  JOB_STATUS_FAILED,
  JOB_STATUS_FINISHED_STATUSES,
} from "@/services/consts"

type reportContentsType = {
  delivery_plan_id: string,
  output_time: string,
  from_date_time: string,
  to_date_time: string,
  date_at_output_time: string,
  start_time_at_output_time: string,
  distance_at_output_time: string,
  x_label: string,
  distance_data: [string, number][],
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Every24HoursReportPreview',
  props: {
    switchContentLoading: { type: Function },
    showFlash: { type: Function }
  },
  setup(__props: any) {

const props = __props

document.title = i18n.global.t('pages.every24HoursReport.page.documentTitle');

const route = useRoute();
const router = useRouter();
const { loginWithRedirect } = useAuth();
const deliveryPlanRecords: Ref<every24HoursPreviewType[]> = ref([])
const isShowableDownloadButton: Ref<boolean> = ref(false);
const downloading: Ref<boolean> = ref(false);
const reportContents: Ref<reportContentsType[]> = ref([]);

onMounted(() => {
  props.switchContentLoading(true)
  getAuthorities()

  const operationOfficeId = String(route.query.operation_office_id ?? "");
  const deliveryPlanIds = String(route.query.delivery_plan_ids ?? "");
  if (checkIncluededRequiredValues(operationOfficeId, deliveryPlanIds)) {
    createJobStatus(makeGetPreviewRequestParams(operationOfficeId, deliveryPlanIds))
  }
})

async function getAuthorities() {
  const authorityList = await AuthorityCacheApi.getWithParse()
  isShowableDownloadButton.value = authorityList["octlink.every_24hours_delivery_report.output"]
}

function createJobStatus(params: any) {
  Api().createJobStatus(params).then((data) => {
    pollingJobStatus(data.id, params)
  }).catch((error) => {
    const res = error.response
    switch (res.status) {
      case 401:
        loginWithRedirect({ appState: { targetUrl: location.pathname } })
        break
      case 403:
        location.reload()
        break
    }
    downloading.value = false
    props.switchContentLoading(false)
  })
}

function makeGetPreviewRequestParams(operationOfficeId: string, deliveryPlanIds: string) {
  return {
    type: EVERY_24_HOUR_REPORT_PREVIEW_TYPE,
    operation_office_id: operationOfficeId,
    delivery_plan_ids: deliveryPlanIds
  }
}

function checkIncluededRequiredValues(operationOfficeId: string, deliveryPlanIds: string) {
  if (!operationOfficeId || !deliveryPlanIds) {
    router.push({ name: "Every24hoursReport" });
    props.showFlash(i18n.global.t('common.invalidURL'), "error", false);
    return false;
  }
  return true;
}

function pollingJobStatus(id: number, params: any) {
  const interval = setInterval(() => {
    Api().getJobStatus(id).then((res) => {
      if (!JOB_STATUS_FINISHED_STATUSES.includes(res.status)) {
        return;
      }
      if(res.status == JOB_STATUS_SUCCESS) {
        if (params.type == EVERY_24_HOUR_REPORT_PREVIEW_TYPE) {
          deliveryPlanRecords.value = res.output_contents
        } else if (params.type == EVERY_24_HOUR_REPORT_TYPE) {
          downloadFile(res.file_path)
        }
      } else if (res.status == JOB_STATUS_FAILED) {
        alert(res.output_contents?.message)
      }
      clearInterval(interval)
      props.switchContentLoading(false)
      downloading.value = false
    })
  }, 3000)
}

function download() {
  downloading.value = true
  createJobStatus(makeDownloadFileRequestParams())
}

function makeDownloadFileRequestParams() {
  return {
    type: EVERY_24_HOUR_REPORT_TYPE,
    operation_office_id: route.query.operation_office_id,
    report_contents: JSON.stringify(reportContents.value),
  }
}

function downloadFile(path: string) {
  Api().fileDownload(path)
}

function changeDetailData(data: reportContentsType) {
  const index = deliveryPlanRecords.value.findIndex((item: every24HoursPreviewType) => {
    return item.delivery_plan_id === data.delivery_plan_id
  });
  if (index !== -1) {
    reportContents.value[index] = {
      delivery_plan_id: data.delivery_plan_id,
      output_time: data.output_time,
      from_date_time: data.from_date_time,
      to_date_time: data.to_date_time,
      date_at_output_time: data.date_at_output_time,
      start_time_at_output_time: data.start_time_at_output_time,
      distance_at_output_time: data.distance_at_output_time,
      driving_time_at_output_time: data.driving_time_at_output_time,
      x_label: data.x_label,
      distance_data: data.distance_data,
    };
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!!deliveryPlanRecords.value.length)
      ? (_openBlock(), _createBlock(PreviewBody, {
          key: 0,
          deliveryPlanRecords: deliveryPlanRecords.value,
          downloading: downloading.value,
          isShowableDownloadButton: isShowableDownloadButton.value,
          onDownload: download,
          onChangeDetailData: changeDetailData
        }, null, 8, ["deliveryPlanRecords", "downloading", "isShowableDownloadButton"]))
      : _createCommentVNode("", true),
    _createVNode(ButtonArea, {
      downloading: downloading.value,
      isShowableDownloadButton: isShowableDownloadButton.value && !!deliveryPlanRecords.value.length,
      onDownload: download
    }, null, 8, ["downloading", "isShowableDownloadButton"])
  ], 64))
}
}

})